import React from 'react';
import styled from 'styled-components';
import CTASection from '../components/CTASection';
import Footer from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';

const HeroSection = styled.section`
  position: relative;
  padding: 140px 20px 110px;
  background: radial-gradient(circle at top left, rgba(12, 77, 162, 0.14), transparent 55%),
    linear-gradient(135deg, rgba(247, 248, 252, 1) 0%, rgba(235, 239, 252, 1) 100%);

  @media (max-width: 768px) {
    padding: 110px 18px 90px;
  }

  @media (max-width: 480px) {
    padding: 96px 16px 80px;
  }
`;

const HeroContent = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 42px;
  align-items: center;

  @media (max-width: 768px) {
    gap: 32px;
  }
`;

const HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    text-align: center;
    align-items: center;
    gap: 18px;
  }
`;

const PageTag = styled.span`
  width: fit-content;
  padding: 6px 16px;
  border-radius: 999px;
  background: rgba(12, 77, 162, 0.16);
  color: var(--primary);
  letter-spacing: 0.32em;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: clamp(2.6rem, 4vw, 3.6rem);
  color: var(--secondary);
  line-height: 1.1;
`;

const PageIntro = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 1.08rem;
  line-height: 1.8;
`;

const HighlightPanel = styled.div`
  padding: 32px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow: var(--shadow-soft);
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 600px) {
    padding: 26px 24px;
    gap: 16px;
  }
`;

const HighlightTitle = styled.h2`
  margin: 0;
  font-size: 1.6rem;
  color: var(--secondary);
`;

const HighlightText = styled.p`
  margin: 0;
  color: var(--text-muted);
  line-height: 1.7;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;

  @media (max-width: 480px) {
    gap: 14px;
  }
`;

const ListItem = styled.li`
  padding: 18px;
  border-radius: var(--radius-md);
  background: rgba(12, 77, 162, 0.08);
  color: var(--secondary);
  font-weight: 600;
  font-size: 0.95rem;
`;

const Section = styled.section`
  padding: 110px 20px;
  background: ${({ $alt }) => ($alt ? '#f7f8fc' : '#ffffff')};

  @media (max-width: 768px) {
    padding: 90px 18px;
  }

  @media (max-width: 480px) {
    padding: 74px 16px;
  }
`;

const SectionInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  display: grid;
  gap: 30px;

  @media (max-width: 768px) {
    gap: 26px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 3vw, 2.8rem);
  color: var(--secondary);
`;

const SectionParagraph = styled.p`
  margin: 0;
  color: var(--text-muted);
  line-height: 1.75;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;

  @media (max-width: 480px) {
    gap: 18px;
  }
`;

const Card = styled.article`
  padding: 28px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 600px) {
    padding: 24px 22px;
    gap: 14px;
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  color: var(--secondary);
`;

const CardText = styled.p`
  margin: 0;
  color: var(--text-muted);
  line-height: 1.7;
  font-size: 0.98rem;
`;

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;

  @media (max-width: 480px) {
    gap: 18px;
  }
`;

const KPIBadge = styled.div`
  padding: 26px;
  border-radius: var(--radius-md);
  background: var(--surface);
  border: 1px solid rgba(226, 232, 240, 0.7);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 600px) {
    padding: 24px;
    gap: 10px;
  }
`;

const KPIValue = styled.span`
  font-size: 2.1rem;
  font-weight: 700;
  color: var(--primary);
`;

const KPILabel = styled.span`
  color: var(--text-muted);
  font-size: 0.95rem;
`;

const ConsultingServicesPage = () => {
  const { t } = useLanguage();
  const consultingFocus = t('pages.consulting.focusList');
  const programPillars = t('pages.consulting.pillars');
  const kpis = t('pages.consulting.kpis');

  return (
    <>
      <HeroSection>
        <HeroContent>
          <HeadingGroup>
            <PageTag>{t('pages.consulting.tag')}</PageTag>
            <PageTitle>{t('pages.consulting.title')}</PageTitle>
            <PageIntro>{t('pages.consulting.intro')}</PageIntro>
          </HeadingGroup>
          <HighlightPanel>
            <HighlightTitle>{t('pages.consulting.highlightTitle')}</HighlightTitle>
            <HighlightText>{t('pages.consulting.highlightText')}</HighlightText>
            <List>
              {consultingFocus.map(item => (
                <ListItem key={item}>{item}</ListItem>
              ))}
            </List>
          </HighlightPanel>
        </HeroContent>
      </HeroSection>
      <Section>
        <SectionInner>
          <SectionTitle>{t('pages.consulting.programsTitle')}</SectionTitle>
          <SectionParagraph>{t('pages.consulting.programsIntro')}</SectionParagraph>
          <Columns>
            {programPillars.map(pillar => (
              <Card key={pillar.title}>
                <CardTitle>{pillar.title}</CardTitle>
                <CardText>{pillar.text}</CardText>
              </Card>
            ))}
          </Columns>
        </SectionInner>
      </Section>
      <Section $alt>
        <SectionInner>
          <SectionTitle>{t('pages.consulting.kpiTitle')}</SectionTitle>
          <SectionParagraph>{t('pages.consulting.kpiIntro')}</SectionParagraph>
          <KPIGrid>
            {kpis.map(kpi => (
              <KPIBadge key={kpi.label}>
                <KPIValue>{kpi.value}</KPIValue>
                <KPILabel>{kpi.label}</KPILabel>
              </KPIBadge>
            ))}
          </KPIGrid>
        </SectionInner>
      </Section>
      <CTASection />
      <Footer />
    </>
  );
};

export default ConsultingServicesPage;
